import { useState, useMemo } from 'react';
import { Typography, Box, Card, CardContent, Button, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { queryEvents, queryItemTypesWithLocations, queryPackingOperations, queryTrips, PackingOperation, Event } from '../schema';
import { useEventIdToTrips } from '../hooks/useEventIdToTrips';
import CreateOperationDialog from './CreateOperationDialog';
import ActiveEventsList from './ActiveEventsList';

export default function ActiveInventoryContent() {
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const navigate = useNavigate();

    const { data: eventsData } = queryEvents();
    const { data: itemTypesData } = queryItemTypesWithLocations();
    const { data: operationsData } = queryPackingOperations();
    const { data: tripsData } = queryTrips();

    const events = useMemo(() => eventsData?.events || [], [eventsData]);
    const eventIdToTrips = useEventIdToTrips(tripsData);
    const operations = useMemo(() => operationsData?.packingOperations || [], [operationsData]);

    const activeOperation = useMemo(() => {
        return operations.find((op: PackingOperation) => op.status === 'active') || null;
    }, [operations]);

    const eventsWithInventory = useMemo(() => {
        if (!itemTypesData?.itemTypes) return [];
        const eventIds = new Set<string>();
        for (const itemType of itemTypesData.itemTypes) {
            const itemLocations =
                (itemType as { itemLocations?: Array<{ event?: { id: string }; quantity?: number }> }).itemLocations || [];
            for (const location of itemLocations) {
                if (location.event?.id && (location.quantity || 0) > 0) {
                    eventIds.add(location.event.id);
                }
            }
        }
        return events.filter(e => eventIds.has(e.id));
    }, [events, itemTypesData]);

    const totals = useMemo(() => {
        let totalItems = 0;
        const now = new Date();
        const endedEventIds = new Set<string>();

        if (itemTypesData?.itemTypes) {
            for (const itemType of itemTypesData.itemTypes) {
                const itemLocations =
                    (itemType as { itemLocations?: Array<{ event?: { id: string }; quantity?: number }> }).itemLocations || [];
                for (const location of itemLocations) {
                    const evt = location.event;
                    const evtId = evt?.id;
                    if (evtId && (location.quantity || 0) > 0) {
                        const event = events.find(e => e.id === evtId);
                        if (event) {
                            totalItems += location.quantity || 0;
                            if (event.endDate && new Date(event.endDate) < now) {
                                endedEventIds.add(event.id);
                            }
                        }
                    }
                }
            }
        }

        return { totalItems, endedEventsCount: endedEventIds.size };
    }, [itemTypesData, events]);

    const endedEventsWithItems = useMemo(() => {
        const now = new Date();
        return eventsWithInventory.filter((event: Event) => {
            if (!event.endDate) return false;
            return new Date(event.endDate) < now;
        });
    }, [eventsWithInventory]);

    return (
        <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    Events with Inventory
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    Track which events currently have your equipment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {eventsWithInventory.length} {eventsWithInventory.length === 1 ? 'event' : 'events'}
                </Typography>
            </Box>

            {endedEventsWithItems.length > 0 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {endedEventsWithItems.length} {endedEventsWithItems.length === 1 ? 'event has' : 'events have'} ended but still have
                    inventory. Items may be left behind.
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', minWidth: 0 }}>
                <Card sx={{ flex: '1 1 200px', minWidth: { xs: 0, sm: 200 } }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            Events with Items
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            {eventsWithInventory.length}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            Total Items
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            {totals.totalItems}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: '1 1 200px', minWidth: { xs: 0, sm: 200 } }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            Events Past End Date
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 600, color: totals.endedEventsCount > 0 ? 'warning.main' : 'text.primary' }}
                        >
                            {totals.endedEventsCount}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: '1 1 200px', minWidth: { xs: 0, sm: 200 } }}>
                    <CardContent>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Quick Action
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => setCreateDialogOpen(true)}
                                sx={{ mt: 1 }}
                            >
                                Create Operation
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {eventsWithInventory.length > 0 ? (
                <ActiveEventsList
                    events={eventsWithInventory}
                    activeOperation={activeOperation}
                    itemTypesData={itemTypesData}
                    eventIdToTrips={eventIdToTrips}
                />
            ) : (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            No events currently have inventory
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            All items are at HQ
                        </Typography>
                        <Button variant="outlined" onClick={() => navigate('/inventory/items')}>
                            View Items
                        </Button>
                    </CardContent>
                </Card>
            )}

            <CreateOperationDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} />
        </Box>
    );
}
